import React from "react";

import type { CreateRecurringSeriesRequestJson, ExtendedWorkspace } from "@performyard/service-contracts";

import MeetingForm, { type MeetingFormData, type MeetingRecurrenceFormData } from "./MeetingForm";
import { VideoConferencingErrorModal } from "./VideoConferencingErrorModal";

import { useAddWorkspaceRecurringSeries } from "../../../../../api/hooks";
import { createErrorModalResubmit, useVideoConferencingErrorHandler } from "./hooks";

import { createRecurringMeetingFormSchema } from "./meeting-form-schema";
import { buildRecurrenceFromForm, getDefaultFormValues } from "./meeting-form-utils";
import { errorModalActions, useMeetingFormState, useRunOnOpen } from "./use-meeting-form-modal";

type CreateRecurringSeriesModalProps = {
	isOpen: boolean;
	handleClose: () => void;
	workspace: ExtendedWorkspace;
};

export const CreateRecurringSeriesModal = ({
	isOpen,
	handleClose,
	workspace,
}: CreateRecurringSeriesModalProps): React.ReactElement => {
	const {
		modalState: vcModalState,
		handleVideoConferencingError,
		closeModal: closeVcModal,
		setRetrying: setVcRetrying,
	} = useVideoConferencingErrorHandler();

	const { mutate: addWorkspaceRecurringSeries, isPending } = useAddWorkspaceRecurringSeries(
		handleClose,
		(error, variables) =>
			handleVideoConferencingError(error, {
				operation: "createSeries",
				seriesData: variables.data,
				workspaceId: variables.workspaceId,
			})
	);
	const resubmitAfterVcError = createErrorModalResubmit(addWorkspaceRecurringSeries, setVcRetrying, closeVcModal);

	const { formValues, validationErrors, updateForm, setNewDefaultValues, validateForm, resetValidationErrors } =
		useMeetingFormState(createRecurringMeetingFormSchema);

	useRunOnOpen(isOpen, () => {
		setNewDefaultValues({ ...getDefaultFormValues(), workspaceId: workspace._id });
		resetValidationErrors();
	});

	const handleSubmit = (data: MeetingFormData, recurrence: MeetingRecurrenceFormData): void => {
		if (!validateForm()) {
			return;
		}

		const meetingParticipants = workspace.participants?.map((participant) => ({
			userId: participant.userId,
			role: participant.role,
		}));

		const formattedData: CreateRecurringSeriesRequestJson = {
			name: data.name,
			description: data.description,
			startDateTime: data.startDateTime.toISOString(),
			endDateTime: data.endDateTime.toISOString(),
			participants: meetingParticipants,
			recurrence: buildRecurrenceFromForm(recurrence.repeats, data.startDateTime),
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			seriesEndDate: recurrence.seriesEndDate?.toISOString(),
			carryOver: data.carryOver,
			addToCalendar: data.addToCalendar,
			// The form holds the chosen provider (null = none); omit it from the request when none.
			videoConferencingProvider: data.videoConferencingProvider ?? undefined,
		};

		addWorkspaceRecurringSeries({ workspaceId: workspace._id, data: formattedData });
	};

	const onClose = (): void => {
		if (vcModalState.type !== "none") {
			closeVcModal();
			return;
		}
		handleClose();
		resetValidationErrors();
	};

	const failedVariables =
		vcModalState.type === "error" && vcModalState.context.operation === "createSeries"
			? { workspaceId: vcModalState.context.workspaceId, data: vcModalState.context.seriesData }
			: undefined;

	return (
		<>
			{vcModalState.type === "none" && (
				<MeetingForm
					isOpen={isOpen}
					onClose={onClose}
					onSubmit={handleSubmit}
					formValues={formValues}
					onFormChange={updateForm}
					validationErrors={validationErrors}
					submitLabel="Create Recurring Meeting"
					isPending={isPending}
					title="Create Recurring Meeting"
					showCarryOver
					carryOverVariant="series"
					showMeetingIntegrations={false}
					showSeriesCalendarToggle
					showSeriesVideoConferencing
					showRecurrence
				/>
			)}

			<VideoConferencingErrorModal
				modalState={vcModalState}
				{...errorModalActions(failedVariables, resubmitAfterVcError, (variables) => ({
					...variables,
					data: { ...variables.data, videoConferencingProvider: undefined },
				}))}
				onClose={closeVcModal}
			/>
		</>
	);
};
